import { z } from "zod"

import { escapeHtml } from "./escape-html"

export const CONTACT_FORM_FIELD_TYPES = [
	"shortText",
	"longText",
	"dropdown",
	"checkbox",
] as const

export type ContactFormFieldType = (typeof CONTACT_FORM_FIELD_TYPES)[number]

export const MAX_CONTACT_FORM_FIELDS = 20
export const MAX_CONTACT_FORM_ANSWER_LENGTH = 5000

export const ContactFormFieldSchema = z.object({
	id: z.string().min(1).max(64),
	label: z.string().max(200),
	type: z.enum(CONTACT_FORM_FIELD_TYPES),
	required: z.boolean(),
	options: z.array(z.string().max(200)).max(50).optional(),
})

export type ContactFormField = z.infer<typeof ContactFormFieldSchema>

export const ContactFormAnswersSchema = z.record(
	z.union([z.string().max(MAX_CONTACT_FORM_ANSWER_LENGTH), z.boolean()]),
)

export type ContactFormAnswers = z.infer<typeof ContactFormAnswersSchema>

/** Reads stored field config, dropping entries that no longer match the shape. */
export function parseContactFormFields(raw: unknown): ContactFormField[] {
	if (!Array.isArray(raw)) return []
	return raw
		.flatMap((entry) => {
			const parsed = ContactFormFieldSchema.safeParse(entry)
			if (!parsed.success) return []
			const field = parsed.data
			// A required checkbox would force every customer to tick it.
			return [
				{ ...field, required: field.type !== "checkbox" && field.required },
			]
		})
		.slice(0, MAX_CONTACT_FORM_FIELDS)
}

/** The fields a customer sees: a half-written field stays hidden until it has a label and options. */
export function visibleContactFormFields(raw: unknown): ContactFormField[] {
	return parseContactFormFields(raw).flatMap((field) => {
		if (!field.label.trim()) return []
		if (field.type !== "dropdown") return [field]
		const options = (field.options ?? []).filter((option) => option.trim())
		return options.length > 0 ? [{ ...field, options }] : []
	})
}

function answerText(
	field: ContactFormField,
	value: string | boolean | undefined,
): string | null {
	if (field.type === "checkbox") return value === true ? "Yes" : null
	if (typeof value !== "string") return null
	const text = value.trim()
	if (!text) return null
	if (field.type === "dropdown" && !field.options?.includes(text)) return null
	return text
}

/** Ids of the required fields the customer left empty. */
export function missingContactFormAnswers(
	fields: ContactFormField[],
	answers: ContactFormAnswers,
): string[] {
	return fields
		.filter((field) => field.required && !answerText(field, answers[field.id]))
		.map((field) => field.id)
}

/** One escaped `<p>` per answered field, in the configured order, to put above the message. */
export function contactFormAnswersHtml(
	rawFields: unknown,
	answers: ContactFormAnswers | null | undefined,
): string {
	if (!answers) return ""
	return visibleContactFormFields(rawFields)
		.flatMap((field) => {
			const text = answerText(field, answers[field.id])
			if (!text) return []
			const value = escapeHtml(text).replaceAll("\n", "<br />")
			return [
				`<p><strong>${escapeHtml(field.label.trim())}:</strong> ${value}</p>`,
			]
		})
		.join("")
}
